










































import { Component, Vue } from 'vue-property-decorator';
import Invoice from '@/components/Invoicing/Invoice/Invoice.vue';
import { GET_INVOICE } from '@/queries/Invoice';
import cloneDeep from 'lodash/cloneDeep';
import InvoiceShowControls from '@/components/Invoicing/Invoice/Controls/InvoiceShowControls.vue';
import NewInvoiceDeliveryDialog from '@/components/Invoicing/Invoice/Controls/NewInvoiceDeliveryDialog.vue';
import { sortBy, toPascalCase } from '@/utils/utils';
import { GQL_invoice_invoice } from '@/queries/__generated__/invoice';
import { GQL_invoiceChanged, GQL_invoiceChanged_invoiceChanged_invoice_emailLogs } from '@/queries/__generated__/invoiceChanged';
import { watchInvoice, keepWatching, watchedInvoiceId } from '@/app/invoiceWatch';
import i18n from '@/i18n';
import gql from 'graphql-tag';

const INVOICE_CHANGED = gql`
  subscription invoiceChanged($invoiceId: ID!) {
    invoiceChanged(invoiceId: $invoiceId) {
      invoice {
        id
        isDeliveredToFb
        emailLogs {
          id destination sentAt
          deliveryMethod
          sentBy { id name }
          deliveryStatus
          deliveryError
          attachmentsCount
        }
      }
    }
  }
`;

type secondInvoiceChangedArg = {
  subscriptionData: subscriptionData;
}
type subscriptionData = {
  data: GQL_invoiceChanged;
}

@Component({
  components: {
    Invoice,
    InvoiceShowControls,
    NewInvoiceDeliveryDialog,
  },
})
export default class InvoicesShow extends Vue {
  private GET_INVOICE = GET_INVOICE;
  private INVOICE_CHANGED = INVOICE_CHANGED;
  private cloneDeep = cloneDeep;

  // true only when we arrived here straight after creating the invoice
  private justCreated = false;

  private created() {
    // $route is only available from the created hook onwards, not in field initializers
    this.justCreated = this.$route.query.created === 'true';

    // A just-created invoice is on its way to fakturaBank, and an opted-in
    // customer is being delivered to server side, so start listening straight
    // away. Every other way in starts quiet until the user sends something.
    if (this.justCreated) {
      watchInvoice(this.$route.params.id);
    }
  }

  // The subscription is opened by mounting the component and closed by
  // unmounting it, so gating it here is what connects and disconnects.
  private get isListening() {
    return watchedInvoiceId() === this.$route.params.id;
  }

  private mounted() {
    if (this.justCreated) {
      // remove the flag so a refresh / revisit doesn't reopen the dialog
      this.$router.replace({ name: 'InvoicesShow', params: { id: this.$route.params.id } });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private invoiceChanged(_previousData: any, { subscriptionData }: secondInvoiceChangedArg, invoice: GQL_invoice_invoice) {
    const subsciptionInvoice = subscriptionData.data.invoiceChanged.invoice;

    // captured before the overwrite, so we can tell what actually moved
    const before = new Map(invoice.emailLogs.map(log => [ log.id, log.deliveryStatus ]));

    invoice.emailLogs = subsciptionInvoice.emailLogs;
    invoice.isDeliveredToFb = subsciptionInvoice.isDeliveredToFb;

    // Logs the page already knew about are skipped, which is what keeps these
    // notifications from appearing for the delivery history on page load.
    const changed = subsciptionInvoice.emailLogs
      .filter(log => before.get(log.id) !== log.deliveryStatus);

    if (changed.length === 0) return;

    // something is still happening, so hold the subscription open a while longer
    keepWatching(this.$route.params.id);

    // One push can move several logs at once — the server flips every pending
    // EHF delivery in a single commit — so notify per log rather than for the
    // newest one only.
    sortBy(changed, log => [ [ parseInt(log.id), 'asc' ] ])
      .forEach(log => this.displayDeliveryNotification(log));
  }

  private displayDeliveryNotification(log: GQL_invoiceChanged_invoiceChanged_invoice_emailLogs) {
    // Reuses the labels the delivery log list already shows, so every method
    // and status is covered without a second set of translations to keep in step.
    const method = i18n.t(`Invoice.DeliveryLogs.DeliveryMethod.${log.deliveryMethod}`);

    // The bare server error ("Please contact support") does not say what it was
    // about, so frame it like the other notices — which delivery it belongs to.
    if (log.deliveryStatus === 'failed') {
      this.$sMessage.error(i18n.t('Invoice.DeliveryLogs.statusChanged', { method, status: log.deliveryError }));
      return;
    }

    // A dismissal is something the user just did themselves, so saying it back
    // to them is only noise.
    if (log.deliveryStatus === 'failure_ignored') return;

    const statusKey = `Invoice.DeliveryLogs.DeliveryStatus.${toPascalCase(log.deliveryMethod)}.${log.deliveryStatus}`;
    if (!i18n.te(statusKey)) return;

    const message = i18n.t('Invoice.DeliveryLogs.statusChanged', { method, status: i18n.t(statusKey) });

    // A delivery moving forward is worth noticing, so hold it longer than the
    // default — it otherwise disappears before the user can read it.
    if (log.deliveryStatus === 'delivered') {
      this.$sMessage.success(message, { duration: 10000 });
    } else {
      this.$sMessage.info(message, { duration: 10000 });
    }
  }
}
